import { useState } from "react";
import { useSnapshotDiff } from "../api/use-snapshot-diff.js";
import { formatBytes } from "./asset-helpers.js";

// 3.B.3.4: Diff-preview folder-restore-modal:iin ennen vahvistusta.
// Vertailee restore-kohde-snapshot:in vs uusimpaan snapshot:iin → näyttää
// käyttäjälle mitä restore tuo takaisin (uusia tiedostoja, korvattuja
// versioita, identtisiä).
//
// Selvennys: kratti restore EI poista tiedostoja kohdepolusta — restore
// vain lisää ja korvaa. Removed-tiedostot näytetään informatiivisina
// (ne ovat "tulossa takaisin" jos kohde on tyhjä).

export type RestoreDiffPreviewProps = {
  machineId: string;
  /** Restore-lähde snapshot. */
  snapshotId: string;
  /** Vertailupiste — uusin snapshot. Jos sama kuin snapshotId, ei eroja. */
  baselineSnapshotId: string;
  /** Optional sub-polku — rajaa diff:n alipuuhun. */
  sourcePathFilter: string | null;
};

export function RestoreDiffPreview({
  machineId,
  snapshotId,
  baselineSnapshotId,
  sourcePathFilter,
}: RestoreDiffPreviewProps) {
  const diff = useSnapshotDiff(machineId, snapshotId, baselineSnapshotId, sourcePathFilter);
  const [showDetails, setShowDetails] = useState(false);

  if (snapshotId === baselineSnapshotId) {
    return (
      <p className="text-xs text-slate-500">
        Restore palauttaa uusimman snapshot:in — sama sisältö kuin nykyhetkellä.
      </p>
    );
  }

  if (diff.status === "loading") {
    return <p className="text-xs text-slate-500">Lasketaan diff:ää…</p>;
  }
  if (diff.status === "failed") {
    return (
      <p className="text-xs text-yellow-300">
        Diff-haku ei onnistunut: {diff.error}. Restore on silti mahdollinen.
      </p>
    );
  }
  if (diff.status !== "completed") return null;

  const { added, modified, removed, summary } = diff.data;
  // Restore-näkökulmasta:
  //   - "removed" baseline-vs-snapshot = tiedostoja jotka olivat snapshot:issa
  //     mutta puuttuvat baseline:sta → restore PALAUTTAA ne (lisää)
  //   - "added" baseline-vs-snapshot = tiedostoja jotka eivät olleet snapshot:issa
  //     mutta on baseline:ssa → restore EI tuo niitä (eivät snapshot:issa)
  //   - "modified" = sama polku, eri sisältö → restore korvaa baseline-version
  //     snapshot-versiolla
  //
  // Tarkoitus käyttäjälle:
  //   - "+ restore tuo takaisin": removed (snapshot:issa mutta ei nyt)
  //   - "~ restore korvaa": modified
  //   - "≡ pysyy ennallaan": tarkkaa lukua emme tiedä — implisiittinen
  const restoreAdds = removed.length;
  const restoreReplaces = modified.length;
  const totalRestoreBytes = summary.removedBytes + summary.modifiedBytes;

  if (restoreAdds === 0 && restoreReplaces === 0) {
    return (
      <p className="text-xs text-slate-500">
        Restore ei muuta mitään — kaikki tiedostot ovat jo identtisinä baseline:ssa.
      </p>
    );
  }

  return (
    <div className="space-y-2">
      <div className="rounded-md border border-slate-800 bg-slate-900/40 px-3 py-2 text-sm text-slate-200">
        <p className="font-medium">Tämä restore tuo takaisin:</p>
        <ul className="mt-1 space-y-0.5 text-xs">
          {restoreAdds > 0 && (
            <li>
              <span className="text-emerald-300">+{restoreAdds.toLocaleString("fi-FI")}</span>{" "}
              tiedostoa joita ei nyt ole ({formatBytes(summary.removedBytes)})
            </li>
          )}
          {restoreReplaces > 0 && (
            <li>
              <span className="text-yellow-300">~{restoreReplaces.toLocaleString("fi-FI")}</span>{" "}
              tiedostoa korvautuu eri versiolla ({formatBytes(summary.modifiedBytes)})
            </li>
          )}
        </ul>
        <p className="mt-2 text-xs italic text-slate-500">
          Kratti restore EI poista tiedostoja kohdepolusta — vain lisää ja korvaa.
        </p>
        <p className="mt-1 text-xs text-slate-500">
          Yhteensä tuotuna: {formatBytes(totalRestoreBytes)}
        </p>
      </div>
      <button
        type="button"
        onClick={() => setShowDetails((v) => !v)}
        className="text-xs text-slate-400 underline hover:text-slate-200"
      >
        {showDetails ? "Piilota yksityiskohdat" : "Näytä yksityiskohdat"}
      </button>
      {showDetails && (
        <div className="max-h-64 overflow-y-auto rounded-md border border-slate-800 bg-slate-900/40 px-3 py-2 text-xs">
          <ul className="space-y-0.5 font-mono">
            {removed.map((e) => (
              <li key={`r-${e.path}`} className="text-emerald-300">
                ⊕ {e.path}{" "}
                <span className="text-slate-500">({formatBytes(e.size)})</span>
              </li>
            ))}
            {modified.map((e) => (
              <li key={`m-${e.path}`} className="text-yellow-300">
                ✏ {e.path}{" "}
                <span className="text-slate-500">
                  ({formatBytes(e.sizeNew)} → {formatBytes(e.sizeOld)})
                </span>
              </li>
            ))}
            {added.length > 0 && (
              <li className="mt-2 border-t border-slate-800 pt-2 text-slate-500">
                Huom: {added.length} tiedostoa on baseline:ssa mutta EI snapshot:issa
                — restore EI tuo niitä (eivät kuulu palautettavaan versioon).
              </li>
            )}
          </ul>
        </div>
      )}
    </div>
  );
}
